/**
 * Sanitiza a resposta de uma IA para extrair apenas o objeto JSON.
 * Remove blocos de código markdown (```json ... ```) e espaços extras.
 */
export const sanitizeAIJSON = (text: string): string => {
  if (!text) return '';
  
  let rawJson = text.trim();
  
  // 1. Tenta extrair o conteúdo do primeiro bloco de código markdown ```json ... ``` se presente
  const codeBlockMatch = rawJson.match(/```(?:json)?\s*([\s\S]*?)\s*```/i);
  if (codeBlockMatch && codeBlockMatch[1]) {
    rawJson = codeBlockMatch[1].trim();
  }

  // 2. Extrai a substring delimitada pelas chaves { ... } ou colchetes [ ... ] mais externos
  const firstBrace = rawJson.indexOf('{');
  const firstBracket = rawJson.indexOf('[');
  let startIdx = -1;

  if (firstBrace !== -1 && firstBracket !== -1) {
    startIdx = Math.min(firstBrace, firstBracket);
  } else if (firstBrace !== -1) {
    startIdx = firstBrace;
  } else if (firstBracket !== -1) {
    startIdx = firstBracket;
  }

  if (startIdx !== -1) {
    const isObject = rawJson[startIdx] === '{';
    const lastIdx = isObject ? rawJson.lastIndexOf('}') : rawJson.lastIndexOf(']');
    if (lastIdx > startIdx) {
      rawJson = rawJson.slice(startIdx, lastIdx + 1);
    }
  } else {
    rawJson = rawJson
      .replace(/```json/gi, '')
      .replace(/```/g, '')
      .trim();
  }

  // 3. Sanitiza expressões matemáticas geradas pela IA em propriedades numéricas (ex: "materialCost": 850 + 950 -> "materialCost": 1800)
  return rawJson.replace(/:\s*([\d\.\s\+\-\*\/\(\)]+)(?=[,\}\n])/g, (match, expr) => {
    const trimmed = expr.trim();
    if (/[\+\-\*\/]/.test(trimmed) && /^[\d\.\s\+\-\*\/\(\)]+$/.test(trimmed)) {
      try {
        const val = Function(`"use strict"; return (${trimmed})`)();
        if (typeof val === 'number' && !isNaN(val)) {
          return `: ${val}`;
        }
      } catch {
        // Fallback em caso de erro de parsing
      }
    }
    return match;
  });
};

/**
 * Faz o parse seguro de uma resposta JSON de IA.
 */
export const parseAIJSON = <T>(text: string, defaultValue: T): T => {
  try {
    const sanitized = sanitizeAIJSON(text);
    return JSON.parse(sanitized) as T;
  } catch (error) {
    console.error('Erro ao processar JSON da IA:', error, 'Texto original:', text);
    return defaultValue;
  }
};
